/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2022. All Rights Reserved.
 #*
 #*===================================================================
 */

export class ProfileImage {
  constructor(public _id?: string, public approved?: boolean, public url_s?: string) {}
}

export interface MongoUser {
  // main fields
  _id: string;
  company: any;
  firstName?: string;
  lastName?: string;
  email: string;
  password?: string;
  phoneNumber: string;
  type: USER_TYPES;

  // contact data
  fullName?: string;
  activationCode: string;
  hasPassword?: boolean;
  phoneNumberExt: string;
  cellPhone: string;
  officePhoneNumber: string;
  departments: USER_DEPARTMENT[];
  roles: string[];
  isActive: boolean;
  paidUser: boolean;
  isApproved: boolean;
  isDeleted: boolean;
  isSupportAccount?: boolean;
  selfOnboarded?: boolean;
  emailNotifications?: unknown[];
  emailNotificationsEnabled: boolean;
  addresses?: unknown[];
  address2?: string;
  profileImage: ProfileImage;
  channelId: string;
  androidChannelId: string;
  disabledNotifications: unknown[];
  notifications: unknown[];
  driverAssignedEquipment?: unknown;
  driverDesignation?: unknown;
  driverLicense?: any;
  driverRate?: unknown;
  emergencyContacts?: unknown[];
  loggedInto: unknown;
  onboardingCompleted: boolean;
  // last known position of the driver
  lastLogin?: Date;
  geoLocation: {
    lat: number;
    long: number;
    city: string;
    state: string;
    distanceToNextStop: number;
    nextStopId: string;
    isAtFinalDropOff: boolean;
    date: Date;
    loadId?: any;
  };
  quickBooks: unknown;
  triumphCredentials: unknown;
  settings: unknown;
  smsSent?: Date;
  laneQuoteSelectedCustomer: string;
  selectedLanguage: string;
  eSignature: unknown;
  apiIntegrations?: IApiIntegrations;
  tutorials?: string[];

  createdAt?: Date;
  updatedAt?: Date;
  passwordUpdatedAt?: Date;
  dialpadToken?: string | null;
  isCloned?: boolean;
  originalId?: string;
  rocketChatUser?: any;
  chatUser?: string;
  // efw requirement
  externalSalesPersonId?: string;
}

export enum USER_TYPES {
  BROKER = 'broker',
  SHIPPER = 'shipper',
  CARRIER = 'carrier',
  FLEET_MANAGER = 'fleet_manager',
  CARRIER_ACCOUNTANT = 'carrier_accountant',
  CARRIER_ADMIN = 'carrier_admin',
  CARRIER_DISPATCHER = 'carrier_dispatcher',
  CARRIER_OPS_MANAGER = 'carrier_ops_manager',
  DRIVER = 'driver',
  SUPER_ADMIN_MANAGER = 'super_admin_manager',
}
export enum USER_ROLES {
  // deprecated
  MANAGER = 'manager',
  ADMINISTRATOR = 'administrator',
  ACCOUNTING = 'accounting',
  VIEWER = 'view_only',
  CARRIER_RELATIONS = 'carrier relations',
  // active for shp/fm
  FLEET_ADMIN = 'fleet_admin',
  SHIPPER_ADMIN = 'shipper_admin',
  MEMBER = 'member',
  SHIPPER_TMS_OWNER = 'shipper_tms_owner',
  SHIPPER_TMS_ADMIN = 'shipper_tms_admin',
  SHIPPER_TMS_MANAGER = 'shipper_tms_manager',
  SHIPPER_TMS_MEMBER = 'shipper_tms_member',
  // Broker roles
  ACCOUNT_MANAGER_FOR_CUSTOMERS = 'Account Manager For Customers',
  ACCOUNTING_MANAGER = 'Accounting Manager',
  OPERATIONS_MANAGER = 'Operations Manager',
  SALES_MANAGER = 'Sales Manager',
  PRICING = 'Pricing',
}
export const mapUserRoles = new Map([
  // to be removed after fix
  [USER_ROLES.MANAGER, 'Manager'],
  [USER_ROLES.ADMINISTRATOR, 'Administrator'],
  [USER_ROLES.ACCOUNTING, 'Accounting'],
  [USER_ROLES.VIEWER, 'View Only'],
  //
  [USER_ROLES.ACCOUNT_MANAGER_FOR_CUSTOMERS, 'Account Manager'],
  [USER_ROLES.ACCOUNTING_MANAGER, USER_ROLES.ACCOUNTING_MANAGER],
  [USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.OPERATIONS_MANAGER],
  [USER_ROLES.SALES_MANAGER, USER_ROLES.SALES_MANAGER],
  [USER_ROLES.PRICING, USER_ROLES.PRICING],
]);

export enum USER_DEPARTMENT {
  OPERATIONS = 'Operations',
  SALES = 'Sales',
  ACCOUNTING = 'Accounting',
  LOGISTICS = 'Logistics',
  ACH_PAYMENTS = 'ACH payments',
  CREDIT_CARD_PAYMENTS = 'Credit card payments',
  CHECK_PAYMENTS = 'Check payments',
}

export interface IUserDepartmentOption {
  department: USER_DEPARTMENT;
  description?: string;
  subItems?: IUserDepartmentOption[];
}

export interface RolesList {
  role: string;
  info: string;
}

export interface IApiIntegrations {
  dat?: IDatCapabilities;
}

export enum DAT_CAPABILITIES {
  VALID_ACCOUNT = 'validAccount',
  SEARCH = 'search',
  MANAGE_POSTINGS = 'managePostings',
  ALARM_MATCH = 'alarmMatch',
  LOOKUP_SPOT_RATES = 'lookupSpotRates',
  LOOKUP_CONTRACT_RATES = 'lookupContractRates',
  LOOKUP_BASIC_CARRIER_INFO = 'lookupBasicCarrierInfo',
  LOOKUP_PREMIUM_CARRIER_INFO = 'lookupPremiumCarrierInfo',
}

export interface IDatCapabilities {
  [DAT_CAPABILITIES.VALID_ACCOUNT]: boolean;
  [DAT_CAPABILITIES.SEARCH]: boolean;
  [DAT_CAPABILITIES.MANAGE_POSTINGS]: boolean;
  [DAT_CAPABILITIES.ALARM_MATCH]: boolean;
  [DAT_CAPABILITIES.LOOKUP_SPOT_RATES]: boolean;
  [DAT_CAPABILITIES.LOOKUP_CONTRACT_RATES]: boolean;
  [DAT_CAPABILITIES.LOOKUP_BASIC_CARRIER_INFO]: boolean;
  [DAT_CAPABILITIES.LOOKUP_PREMIUM_CARRIER_INFO]: boolean;
}
